import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, MessageCircle, MapPin } from "lucide-react";
import { site, assets, mapsDirectionsUrl, offer, whatsappPrefill } from "@/config/site";
import { EASE } from "./Reveal";

const iconChip =
    "flex h-9 w-9 shrink-0 items-center justify-center border border-rz-red/50 text-rz-red transition-colors duration-300 hover:border-rz-red hover:bg-rz-red hover:text-white";

const LINKS = [
    { label: "Services", href: "#services", testid: "nav-link-services" },
    { label: "Our Work", href: "#work", testid: "nav-link-our-work" },
    { label: "Transformations", href: "#transformations", testid: "nav-link-transformations" },
    { label: "About", href: "#about", testid: "nav-link-about" },
    { label: "Studio", href: "#studio", testid: "nav-link-studio" },
    { label: "Contact", href: "#contact", testid: "nav-link-contact" },
];

export const Nav = ({ started = true }) => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 80);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    return (
        <>
        <header
            className={`fixed inset-x-0 top-0 z-50 transition-[background-color,padding,border-color] duration-500 ${
                scrolled
                    ? "border-b border-rz-red/70 bg-rz-black/85 backdrop-blur-md"
                    : "border-b border-transparent bg-transparent"
            }`}
        >
            <motion.nav
                aria-label="Primary"
                initial={{ y: -20, opacity: 0 }}
                animate={started ? { y: 0, opacity: 1 } : {}}
                transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
                className={`mx-auto flex max-w-[1440px] items-center justify-between px-5 transition-[padding] duration-500 sm:px-8 lg:px-12 ${
                    scrolled ? "py-3" : "py-5"
                }`}
            >
                <a
                    href="#top"
                    data-testid="nav-brand-logo"
                    className="flex min-w-0 items-center gap-2.5"
                    aria-label="Revautoz — back to top"
                >
                    <img src={assets.emblemTransparent} alt="Revautoz RL emblem" className="h-8 w-8 shrink-0 object-contain sm:h-9 sm:w-9" />
                    <span className="shrink-0 font-display text-base font-bold uppercase tracking-[0.14em] text-rz-white sm:text-lg sm:tracking-[0.18em]">
                        Revautoz
                    </span>
                </a>

                <div className="hidden items-center gap-6 xl:flex">
                    {LINKS.map((l, i) => (
                        <motion.a
                            key={l.href}
                            href={l.href}
                            data-testid={l.testid}
                            initial={{ opacity: 0, y: -10 }}
                            animate={started ? { opacity: 1, y: 0 } : {}}
                            transition={{ duration: 0.5, delay: 0.3 + i * 0.06, ease: EASE }}
                            className="group relative font-display text-[11px] font-medium uppercase tracking-[0.16em] text-rz-smoke transition-colors duration-300 hover:text-rz-white"
                        >
                            {l.label}
                            <span className="absolute -bottom-1 left-0 h-px w-0 bg-rz-red transition-[width] duration-300 group-hover:w-full" />
                        </motion.a>
                    ))}
                    <a
                        href={`tel:${site.phone}`}
                        data-testid="nav-call-icon"
                        aria-label={`Call Revautoz on ${site.phoneDisplay}`}
                        className={`${iconChip} nav-pulse`}
                    >
                        <Phone size={15} strokeWidth={1.5} />
                    </a>
                    <a
                        href={site.whatsappLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="nav-whatsapp-icon"
                        aria-label="WhatsApp Revautoz"
                        className={iconChip}
                    >
                        <MessageCircle size={15} strokeWidth={1.5} />
                    </a>
                    <a
                        href={mapsDirectionsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="nav-location-icon"
                        aria-label="Revautoz location on Google Maps"
                        className={iconChip}
                    >
                        <MapPin size={15} strokeWidth={1.5} />
                    </a>
                    <a
                        href={whatsappPrefill(offer.message)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="nav-button-enquire"
                        className="btn-sweep whitespace-nowrap bg-rz-red px-4 py-2.5 font-display text-[11px] font-semibold uppercase tracking-[0.16em] text-white transition-colors duration-300 hover:bg-rz-reddeep"
                    >
                        Free Inspection
                    </a>
                </div>

                <div className="flex shrink-0 items-center gap-2 xl:hidden">
                    <a
                        href={`tel:${site.phone}`}
                        data-testid="nav-mobile-call-icon"
                        aria-label={`Call Revautoz on ${site.phoneDisplay}`}
                        className={`${iconChip} nav-pulse`}
                    >
                        <Phone size={15} strokeWidth={1.5} />
                    </a>
                    <a
                        href={site.whatsappLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="nav-mobile-whatsapp-icon"
                        aria-label="WhatsApp Revautoz"
                        className={iconChip}
                    >
                        <MessageCircle size={15} strokeWidth={1.5} />
                    </a>
                    <a
                        href={mapsDirectionsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="nav-mobile-location-icon"
                        aria-label="Revautoz location on Google Maps"
                        className={iconChip}
                    >
                        <MapPin size={15} strokeWidth={1.5} />
                    </a>
                    <button
                        data-testid="nav-menu-toggle"
                        onClick={() => setOpen(true)}
                        aria-label="Open menu"
                        className={`${iconChip} border-transparent text-rz-white`}
                    >
                        <Menu size={20} strokeWidth={1.5} />
                    </button>
                </div>
            </motion.nav>

        </header>
        <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="mobile-menu"
                        className="fixed inset-0 z-[60] flex flex-col bg-rz-black"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.35, ease: EASE }}
                    >
                        <div className="flex items-center justify-between px-5 py-5 sm:px-8">
                            <span className="font-display text-lg font-bold uppercase tracking-[0.18em]">
                                Revautoz
                            </span>
                            <button
                                data-testid="mobile-menu-close"
                                onClick={() => setOpen(false)}
                                aria-label="Close menu"
                                className="text-rz-white"
                            >
                                <X size={28} strokeWidth={1.5} />
                            </button>
                        </div>
                        <nav aria-label="Mobile" className="flex flex-1 flex-col justify-center gap-2 px-6">
                            {LINKS.map((l, i) => (
                                <motion.a
                                    key={l.href}
                                    href={l.href}
                                    data-testid={`mobile-${l.testid}`}
                                    onClick={() => setOpen(false)}
                                    initial={{ opacity: 0, y: 24 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: EASE }}
                                    className="border-b border-rz-steel/60 py-4 font-display text-4xl font-bold uppercase tracking-tight text-rz-white transition-colors hover:text-rz-red"
                                >
                                    {l.label}
                                </motion.a>
                            ))}
                        </nav>
                        <div className="grid grid-cols-2 border-t border-rz-steel/60">
                            <a
                                data-testid="mobile-menu-call"
                                href={`tel:${site.phone}`}
                                className="flex items-center justify-center gap-2 py-5 font-display text-sm uppercase tracking-[0.15em] text-rz-white"
                            >
                                <Phone size={16} /> Call
                            </a>
                            <a
                                data-testid="mobile-menu-whatsapp"
                                href={site.whatsappLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center gap-2 border-l border-rz-steel/60 py-5 font-display text-sm uppercase tracking-[0.15em] text-rz-white"
                            >
                                <MessageCircle size={16} /> WhatsApp
                            </a>
                        </div>
                    </motion.div>
                )}
        </AnimatePresence>
        </>
    );
};
